import * as THREE from "three";

import { SceneManager } from "../core/scene_manager.js";
import { Barrier } from "./barrier.js";
import { HangConfig, PhysicsTable } from "./physics_table.js";

export const HANG_CONFIG: HangConfig = {
  anchorHeight: 2.4,
  swingDeg: 6,
  swingPeriodSec: 3.2,
};

type CableSide = "left" | "right";

// One straight run of a cable: from the sky down to the top table, or from one
// table down to the next. Built from the barrier's mount-rod tiles laid end to
// end along +X, then turned and stretched onto its run every frame.
interface CableRod
{
  mesh: THREE.Group;
  length: number;
}

export class HangingRig
{
  private static readonly CABLE_SKY_MARGIN = 0.5;
  private static readonly CABLE_FALLBACK_SKY = 60;
  private static readonly ROD_AXIS = new THREE.Vector3( 1, 0, 0 );
  private static readonly tmp_view_projection = new THREE.Matrix4();

  private readonly tables: PhysicsTable[];
  private readonly pivot_world = new THREE.Vector3();
  private readonly anchor_left: THREE.Vector3;
  private readonly anchor_right: THREE.Vector3;
  private readonly rods_left: CableRod[];
  private readonly rods_right: CableRod[];
  private readonly tmp_upper = new THREE.Vector3();
  private readonly tmp_lower = new THREE.Vector3();
  private readonly tmp_dir = new THREE.Vector3();

  constructor( tables: PhysicsTable[], config: HangConfig )
  {
    this.tables = [ ...tables ].sort( ( a, b ) => b.getRestTopBox().max.y - a.getRestTopBox().max.y );

    const top_box = this.tables[0].getRestTopBox();
    const top_center = top_box.getCenter( new THREE.Vector3() );

    this.pivot_world.set( top_center.x, top_box.max.y + config.anchorHeight, top_center.z );

    for ( const table of this.tables )
      table.setHangPivot( this.pivot_world );

    this.anchor_left = new THREE.Vector3( top_box.min.x, HangingRig.skyY( top_box.min.x, top_center.z ), top_center.z );
    this.anchor_right = new THREE.Vector3( top_box.max.x, HangingRig.skyY( top_box.max.x, top_center.z ), top_center.z );

    this.rods_left = this.createCable( "left" );
    this.rods_right = this.createCable( "right" );

    this.updateCables();
  }

  public getTables(): readonly PhysicsTable[]
  {
    return this.tables;
  }

  public getPivotWorld(): THREE.Vector3
  {
    return this.pivot_world;
  }

  public setAngle( rad: number ): void
  {
    for ( const table of this.tables )
      table.setSwingAngle( rad );

    this.updateCables();
  }

  // The tiles share the barrier material and the loaded model's geometry, so
  // only the rods themselves are taken out of the scene.
  public dispose(): void
  {
    for ( const rod of [ ...this.rods_left, ...this.rods_right ] )
      SceneManager.world.remove( rod.mesh );
  }

  private createCable( side: CableSide ): CableRod[]
  {
    return this.tables.map( ( _, index ) =>
    {
      const run = this.runEnds( side, index )
        ? this.tmp_upper.distanceTo( this.tmp_lower )
        : 0;

      const first = Barrier.createRodTile();
      const tiles = Math.max( 1, Math.round( run / first.length ) );
      const mesh = new THREE.Group();

      mesh.add( first.mesh );
      for ( let i = 1; i < tiles; i++ )
      {
        const tile = Barrier.createRodTile();
        tile.mesh.position.x = i * first.length;
        mesh.add( tile.mesh );
      }

      SceneManager.world.add( mesh );

      return { mesh, length: tiles * first.length };
    } );
  }

  private static skyY( x: number, z: number ): number
  {
    const camera = SceneManager.camera;

    camera.updateMatrixWorld();

    const e = HangingRig.tmp_view_projection
      .multiplyMatrices( camera.projectionMatrix, camera.matrixWorldInverse )
      .elements;

    const denominator = e[ 5 ] - e[ 7 ];

    if ( Math.abs( denominator ) < 1e-6 )
      return HangingRig.CABLE_FALLBACK_SKY;

    const y = ( ( e[ 3 ] - e[ 1 ] ) * x + ( e[ 11 ] - e[ 9 ] ) * z + ( e[ 15 ] - e[ 13 ] ) ) / denominator;

    return y + HangingRig.CABLE_SKY_MARGIN;
  }

  // Writes the ends of run `index` into tmp_upper / tmp_lower: the sky anchor
  // or the table above, down to this table's attachment point.
  private runEnds( side: CableSide, index: number ): boolean
  {
    const upper = index === 0
      ? this.tmp_upper.copy( side === "left" ? this.anchor_left : this.anchor_right )
      : this.tables[ index - 1 ].getCableAttach( side, this.tmp_upper );

    return !!upper && !!this.tables[ index ].getCableAttach( side, this.tmp_lower );
  }

  private updateCables(): void
  {
    this.writeCable( this.rods_left, "left" );
    this.writeCable( this.rods_right, "right" );
  }

  private writeCable( rods: CableRod[], side: CableSide ): void
  {
    rods.forEach( ( rod, index ) =>
    {
      const has_ends = this.runEnds( side, index );
      const dir = this.tmp_dir.subVectors( this.tmp_upper, this.tmp_lower );
      const run = dir.length();

      rod.mesh.visible = has_ends && run > 1e-6;
      if ( !rod.mesh.visible )
        return;

      rod.mesh.position.copy( this.tmp_lower );
      rod.mesh.quaternion.setFromUnitVectors( HangingRig.ROD_AXIS, dir.divideScalar( run ) );
      rod.mesh.scale.x = run / rod.length;
    } );
  }
}
